import React, { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import classNames from "classnames";
import { Module, ModuleCaption } from "Components/Module";
import { AccountClient, SsoType, SsoMemberModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { popupSsoLoginCommon } from "Utils/sso";
import UserContext from "../../UserContext";

export interface SSOInformationProps {
}

export const SSOInformation: React.FC<SSOInformationProps> = () => {
   const { user } = useContext(UserContext);
   const [cookies, _setCookie] = useCookies(["sign-in-with-apple-enabled"]);
   const [isLoading, setIsLoading] = useState<boolean>(true);
   const accountClient = useApiClient(AccountClient);
   const [isGoogle, setIsGoogle] = useState<boolean>(false);
   const [google, setGoogle] = useState<SsoMemberModel>();
   const [isLinkedIn, setIsLinkedIn] = useState<boolean>(false);
   const [linkedIn, setLinkedIn] = useState<SsoMemberModel>();
   const [isApple, setIsApple] = useState<boolean>(false);
   const [apple, setApple] = useState<SsoMemberModel>();
   const [isAppleEnabled, setIsAppleEnabled] = useState<boolean>(false);

   const getWidthHeight = (ssoType: SsoType): [number, number] => {
      switch (ssoType) {
         case SsoType.Apple:
            return [440, 678];
         case SsoType.Google:
            return [440, 555];
         case SsoType.LinkedIn:
            return [740, 680];
         default:
            return [440, 600];
      }
   }

   const setUpData = (data: SsoMemberModel[]) => {
      setIsGoogle(data.some(x => x.ssoType == SsoType.Google));
      if (data.some(x => x.ssoType == SsoType.Google)) {
         setGoogle(data.filter(x => x.ssoType == SsoType.Google)[0])
      }
      if (data.some(x => x.ssoType == SsoType.LinkedIn)) {
         setLinkedIn(data.filter(x => x.ssoType == SsoType.LinkedIn)[0])
      }
      if (data.some(x => x.ssoType == SsoType.Apple)) {
         setApple(data.filter(x => x.ssoType == SsoType.Apple)[0])
      }
      setIsLinkedIn(data.some(x => x.ssoType == SsoType.LinkedIn));
      setIsApple(data.some(x => x.ssoType == SsoType.Apple));
      setIsAppleEnabled(data.some(x => x.ssoType == SsoType.Apple) || cookies["sign-in-with-apple-enabled"]);
   }

   const loadData = async (abortSignal?: AbortSignal) => {
      setIsLoading(true);
      try {
         const result = await accountClient.ssoMember(abortSignal);
         if (result) {
            setUpData(result);
         }
      } catch (err) {
         console.log(err);
      }
      setIsLoading(false);
   }

   const removeSso = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, ssoType: SsoType | undefined, ssoId: string | undefined) => {
      e.preventDefault();
      setIsLoading(true);
      try {
         if (ssoType !== undefined && ssoId !== undefined) {
            const abortController = new AbortController();
            const result = await accountClient.removeMemberSSO(ssoType, ssoId, abortController.signal);

            if (result) {
               setUpData(result);
            }
         }
      } catch (err) {
         console.log(err);
      } finally {
         setIsLoading(false);
      }
   }

   const addSso = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, ssoType: SsoType) => {
      e.preventDefault();
      const [width, height] = getWidthHeight(ssoType);
      popupSsoLoginCommon("/Account/StartSsoLogOn?model=" + encodeURIComponent(JSON.stringify({ SsoType: ssoType, MemberIdToLink: user.id })), true, width, height)
   }

   useEffect(() => {
      const abortController = new AbortController();
      loadData(abortController.signal);


      return function cleanup() {
         abortController.abort();
      }
   }, []);

   const linkingListener = (e) => {
      loadData();
   }

   useEffect(() => {
      document.addEventListener("SSO_accountLinking_Completed", linkingListener);
      return function cleanup() {
         document.removeEventListener("SSO_accountLinking_Completed", linkingListener)
      }
   }, [])

   return (
      <Module size="lg" loading={isLoading} className={classNames("module-sso-information")}>
         <ModuleCaption className="mb-px16">
            Social Networks
         </ModuleCaption>
         <div className="sso-information-wrapper">
            <div className="sso-option">
               <div className="sub-caption h7">Google</div>
               <div className="sub-content">
                  {isGoogle && <><a href="" onClick={(e) => removeSso(e, google?.ssoType, google?.ssoId)}>Disconnect</a> to no longer use your Google account to log in</>}
                  {!isGoogle && <><a href="" onClick={(e) => addSso(e, SsoType.Google)}>Connect</a> to log in using your Google account and use your Google profile photo</>}
               </div>
            </div>
            {!user.isSuperUser &&
               <>
                  <div className="sso-option">
                     <div className="sub-caption h7">LinkedIn</div>
                     <div className="sub-content">
                        {isLinkedIn && <><a href="" onClick={(e) => removeSso(e, linkedIn?.ssoType, linkedIn?.ssoId)}>Disconnect</a> to no longer use your LinkedIn account to log in</>}
                        {!isLinkedIn && <><a href="" onClick={(e) => addSso(e, SsoType.LinkedIn)}>Connect</a> to log in using your LinkedIn account, invite your LinkedIn contacts to join and use your profile photo</>}
                     </div>
                  </div>
                  {isAppleEnabled &&
                     <div className="sso-option">
                        <div className="sub-caption h7">Apple</div>
                        <div className="sub-content">
                           {isApple && <><a href="" onClick={(e) => removeSso(e, apple?.ssoType, apple?.ssoId)}>Disconnect</a> to no longer use your Apple account to log in</>}
                           {!isApple && <><a href="" onClick={(e) => addSso(e, SsoType.Apple)}>Connect</a> to log in using your Apple account</>}
                        </div>
                     </div>
                  }
               </>
            }
         </div>
      </Module>
   )
}